import { L10nTranslationService } from 'angular-l10n';
import { DashboardLayout } from '../layout/dashboard-layout';

export const DASHBOARD_VIEWS_VERSION = 1;
export const MAX_SAVED_VIEWS = 20;
export const MAX_VIEW_NAME_LENGTH = 60;

export interface DashboardView {
  /** Unique among the user's views of one dashboard */
  readonly id: string;
  readonly name: string;
  readonly layout: DashboardLayout;
}

/** Everything a user saved for one dashboard; what a `DashboardViewStore` persists. */
export interface DashboardSavedViews {
  readonly version: typeof DASHBOARD_VIEWS_VERSION;
  /** The view the dashboard opens on; null for the built-in default */
  readonly activeViewId: string | null;
  readonly views: readonly DashboardView[];
}

export const EMPTY_SAVED_VIEWS: DashboardSavedViews = Object.freeze({
  version: DASHBOARD_VIEWS_VERSION,
  activeViewId: null,
  views: Object.freeze([]),
});

export type ViewNameProblem = 'required' | 'too-long' | 'taken';

/** Why `name` cannot name a view, or null when it can; `takenNames` are compared case-insensitively. */
export function viewNameProblem(name: string, takenNames: readonly string[]): ViewNameProblem | null {
  const trimmed = name.trim();
  if (!trimmed) {
    return 'required';
  }

  if (trimmed.length > MAX_VIEW_NAME_LENGTH) {
    return 'too-long';
  }

  const lower = trimmed.toLocaleLowerCase();
  return takenNames.some((taken) => taken.trim().toLocaleLowerCase() === lower) ? 'taken' : null;
}

export function viewNameMessage(problem: ViewNameProblem, translation: L10nTranslationService): string {
  switch (problem) {
    case 'required':
      return translation.translate('Dashboard.Views.NameRequired');
    case 'too-long':
      return translation.translate('Dashboard.Views.NameTooLong', { max: MAX_VIEW_NAME_LENGTH });
    case 'taken':
      return translation.translate('Dashboard.Views.NameTaken');
  }
}

/** `base`, or "`base` 2", "`base` 3"… when taken */
export function suggestViewName(base: string, takenNames: readonly string[]): string {
  let name = base;
  let n = 1;

  while (viewNameProblem(name, takenNames)) {
    n++;
    name = `${base} ${n}`;
  }

  return name;
}

/** Where a user's saved views live: one document per dashboard, read and written whole. */
export interface DashboardViewStore {
  /** Unvalidated; null when nothing is saved. Rejects when saved data can't be read, so it isn't overwritten. */
  load(dashboardId: string): Promise<unknown>;
  /** Replaces the stored document; rejects when it could not be persisted */
  save(dashboardId: string, views: DashboardSavedViews): Promise<void>;
}
